import BugsnagPerformance from '@bugsnag/browser-performance';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';

import { normalizeBugsnagContext } from './normalizeContext';
import { bugsnagIgnoreErrors } from '../utils/bugsnagIgnore';

/**
 * Initializes Bugsnag with the runtime appVersion and releaseStage required for
 * source-map symbolication.
 *
 * IMPORTANT: `appVersion` MUST match the value used when uploading source-maps in
 * `scripts/upload-bugsnag-frontend-source-maps.sh` (read from package.json via
 * `npm pkg get version`). The build-time `process.env.APP_VERSION` define in
 * `vite.config.ts` reads the same package.json, guaranteeing alignment. Do NOT
 * remove this — without it, Bugsnag cannot match runtime events to uploaded maps.
 */
export const initBugsnag = (): void => {
  const apiKey = (process.env.DF_REACT_APP_BUGSNAG_API_KEY || process.env.REACT_APP_BUGSNAG_API_KEY) ?? '';
  const appVersion = process.env.APP_VERSION;
  const releaseStage = process.env.REACT_APP_ENV || 'production';

  Bugsnag.start({
    apiKey,
    appVersion,
    releaseStage,
    plugins: [new BugsnagPluginReact()],
    onError: event => {
      const shouldReport = bugsnagIgnoreErrors(event);
      if (!shouldReport) return false;

      // Replace the route parameters Bugsnag put in the default context with placeholders, so one
      // defect groups into one issue instead of one per tenant/charge point/EVSE/payment.
      event.context = normalizeBugsnagContext(event.context ?? window.location.pathname);

      event.addMetadata('browser', {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
        url: window.location.href,
        referrer: document.referrer,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        screen: {
          width: window.screen.width,
          height: window.screen.height,
          colorDepth: window.screen.colorDepth,
        },
      });

      if ('connection' in navigator) {
        const connection = (navigator as any).connection;
        event.addMetadata('network', {
          effectiveType: connection?.effectiveType,
          downlink: connection?.downlink,
          rtt: connection?.rtt,
          saveData: connection?.saveData,
        });
      }

      try {
        const storageInfo = {
          localStorageAvailable: !!window.localStorage,
          sessionStorageAvailable: !!window.sessionStorage,
          localStorageKeys: Object.keys(window.localStorage || {}).length,
        };
        event.addMetadata('storage', storageInfo);
      } catch (e) {
        event.addMetadata('storage', { error: 'Unable to access storage' });
      }

      return true;
    },
  });

  BugsnagPerformance.start({ apiKey });
};
